<template>
<el-dialog class="peiso-modal handl-initialization-container" 
    :visible.sync="isOpen"
    :modal-append-to-body="false"
    :close-on-click-modal='false'
    width="550px"
    :before-close="handleClose"
    center>

        <div class="header-container">
            <span v-if="step === 0">Enter H&L credentials
                <span class="d-block">Choose an existing group or create a new one with client credentials.</span>
            </span>
            <span v-else-if="step === 1">We need to understand your profit target.
                <span class="d-block">There are a couple of ways to do so. It could either be a dollar amount, or a percentage of revenue.</span>
            </span>
            <span v-else-if="step === 2 && categories.length > 0">Select Revenue Group</span>
            <span v-else-if="step === 3 && categories.length > 0">Select Foods</span>
            <span v-else-if="step === 4 && categories.length > 0">Select Beverages</span>
        </div>
        <div class="body-container">
            <!-- Step 0: Group Selection -->
            <template v-if="step === 0">
                <div class="selection-container">
                    <div class="input-label mb-2">Venue or Group Name</div>
                    <el-autocomplete
                        class="w-100 mb-3 input-container"
                        v-model="selectedGroup"
                        :fetch-suggestions="querySearch"
                        placeholder="Search for a group"
                        @change="handleGroupChange"
                        @select="handleGroupSelect"
                        :trigger-on-focus="false"
                    ></el-autocomplete>

                    <template v-if="!isExistingGroup">
                        <div class="input-label mb-2">Handl Venue ID</div>
                        <div class="input-container mt-3">
                            <el-input 
                                v-model="handlVenueId" 
                                type="text" 
                                class="peiso-input-default mb-3" 
                                placeholder="Enter Handl Venue ID">
                            </el-input>
                        </div>
                        
                        <div class="input-label mb-2">Client ID</div>
                        <div class="input-container mt-3">
                            <el-input 
                                v-model="clientId" 
                                type="text" 
                                class="peiso-input-default mb-3" 
                                placeholder="Enter Client ID">
                            </el-input>
                        </div>
                        <div class="input-label mb-2">Client Secret</div>
                        <div class="input-container mt-3">
                            <el-input 
                                v-model="clientSecret" 
                                type="text" 
                                class="peiso-input-default" 
                                placeholder="Enter Client Secret">
                            </el-input>
                        </div>
                    </template>
                </div>
            </template>

            <!-- Step 1: Profit Target -->
            <template v-if="step === 1">
                <div class="selection-container">
                    <el-radio-group v-model="profitType" class="w-100">
                        <el-radio :label="0" class="w-100 mb-2">
                            <span>Dollar Amount ($)</span>
                        </el-radio>
                        <el-radio :label="1" class="w-100">
                            <span>Percentage Amount (%)</span>
                        </el-radio>
                    </el-radio-group>
                </div>

                <div class="input-container mt-3">
                    <el-input 
                        v-model="inputAmount" 
                        type="number" 
                        class="peiso-input-default" 
                        :placeholder="profitType === 0 ? 'Enter dollar amount' : 'Enter percentage amount'">
                    </el-input>
                </div>
            </template>

            <!-- Step 2: Revenue Group -->
            <template v-if="step === 2 && categories.length > 0">
                <span v-if="validationMessage" class="errortext m-bottom-1rem">{{validationMessage}}</span>
                <div class="list-field-group">
                    <el-radio-group v-model="revenueGroupId" class="peiso-checkbox">
                        <el-radio v-for="(item, i) in revenues" :key="`radio-revenue-${i}`" :label="item">{{ item }}</el-radio>
                    </el-radio-group>
                </div>
            </template>

            <!-- Step 3: Foods -->
            <template v-if="step === 3 && categories.length > 0">
                <span v-if="validationMessage" class="errortext m-bottom-1rem">{{validationMessage}}</span>
                <div class="list-field-group">
                    <div class="peiso-checkbox checkbox-select-all mb-1">
                        <el-checkbox label="all" @change="checkedAllFood" v-model="isCheckedFood">{{ isCheckedFood ? 'Unselect All' : 'Select All' }}</el-checkbox>
                    </div>
                    <el-checkbox-group v-model="checkedFood" class="peiso-checkbox" @change="handleSelectedFood">
                        <el-checkbox v-for="(item) in categories" :key="`checkbox-food-${item.id}`" :label="item">{{ item.name }}</el-checkbox>
                    </el-checkbox-group>
                </div>
            </template>

            <!-- Step 4: Beverages -->
            <template v-if="step === 4 && categories.length > 0">
                <span v-if="validationMessage" class="errortext m-bottom-1rem">{{validationMessage}}</span>
                <div class="list-field-group">
                    <div class="peiso-checkbox checkbox-select-all mb-1">
                        <el-checkbox label="all" @change="checkedAllBeverage" v-model="isCheckedBeverage">{{ isCheckedBeverage ? 'Unselect All' : 'Select All' }}</el-checkbox>
                    </div>
                    <el-checkbox-group v-model="checkedBeverage" class="peiso-checkbox" @change="handleSelectedBeverage">
                        <el-checkbox v-for="(item) in categories" :key="`checkbox-beverage-${item.id}`" :label="item">{{ item.name }}</el-checkbox>
                    </el-checkbox-group>
                </div>
            </template>

            <div class="button-container">
                <div class="d-flex gap-10" v-if="step > 0 && categories.length > 0">
                    <el-button type="peiso-default" class="w-100" @click="back" :disabled="isLoading">Back</el-button>
                    <el-button v-if="!isLoading" type="peiso-primary-pink" class="w-100" @click="next">
                        {{ (step === 3 && revenueGroupId !== 'both') || step === 4 ? 'Confirm' : 'Next' }}
                    </el-button>
                    <el-button v-if="isLoading" type="peiso-primary-pink" class="w-100 cursor-disable"><i class="el-icon-loading"></i> Loading...</el-button>
                </div>
                <div v-else>
                    <el-button v-if="!isLoading" type="peiso-primary-pink" class="w-100" @click="next">
                        {{ (categories.length > 0 || step == 0) ? 'Next' : 'Confirm' }}
                    </el-button>
                    <el-button v-if="isLoading" type="peiso-primary-pink" class="w-100 cursor-disable"><i class="el-icon-loading"></i> Loading...</el-button>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<style lang='scss'>
@import 'static/scss/fonts/fonts.scss';
    .handl-initialization-container {
        .header-container {
            span {
                @include montserrat-font(16px, 120%, 600, $white);
                > span {
                    font-size: 14px;
                    font-weight: 400;
                    color: $gray3;
                    margin-top: 10px;
                }
            }
        }
        .body-container {
            .m-bottom-1rem {
                
            }
            .selection-container {
                @include montserrat-font(16px, 120%, 400, $white);
                padding: 1rem;

                .input-label {
                    @include montserrat-font(14px, 120%, 500, $white);
                }

                .el-radio-group {
                    .el-radio {
                        padding: 5px 0rem;

                        .el-radio__inner {
                            width: 24px !important;
                            height: 24px !important;
                        }

                        .el-radio__inner::after {
                            width: 12px !important;
                            height: 11px !important;
                        }
                    }

                    .el-radio__input.is-checked .el-radio__inner {
                        border-color:  $pink1;
                        background-color:  $pink1;
                    }
                }
            }
            .input-container {
                padding: 1rem 0rem;
            }

            .button-container {
                padding: 1rem 0rem;
            }

            .list-field-group {
                margin-top: 1rem;
                position: relative;
                min-height: 200px;
                max-height: 400px;
                overflow: hidden auto;
                border-radius: 5px;
                padding: 20px !important;
                background-color: var(--background-color-3);

                &::-webkit-scrollbar {
                    width: 6px;
                }

                &::-webkit-scrollbar-thumb {
                    background: #434968;
                }

                &::-webkit-scrollbar-thumb:hover {
                    background: #434968;
                }
            }

            .el-input__inner {
                background-color: var(--background-color-3) !important;
                color: $white !important;
                border-color: $gray3 !important;
                height: 50px !important;
                &:hover {
                    border-color: $gray3 !important;
                }
            }

            .errortext {
                color: #ff4c30 !important;
                font-size: 13px !important;
                margin-bottom: 10px !important;
            }

            .checkbox-select-all {
                position: relative;
                left: 0;
                top: 0px;
                width: 100%;
                z-index: 50;
                background-color: var(--background-color-3);
                padding-top: 10px;
            }
        }
    }
</style>

<script>
    import utils from '../../utils/utils'
    import api from '../../api/api'
    import { env } from '../../env'

    export default {
        data() {
            return {
                step: 0,
                profitType: 0,
                isOpen: false,
                inputAmount: '',
                isLoading: false,
                validationMessage: '',
                revenueGroupId: 'both',
                revenues: ['both', 'food', 'beverage'],
                categories: [],
                isCheckedFood: false,
                isCheckedBeverage: false,
                checkedFood: [],
                checkedBeverage: [],
                selectedGroup: '',
                isExistingGroup: false,
                handlGroup: '',
                handlVenueId: '',
                clientId: '',
                clientSecret: '',
                groupList: []
            }
        },
        props: {
            open: {
                default: false
            }
        },
        watch: {
            async open(val) {
                this.isOpen = val
                await this.getHandlRevenueGroups()
            }
        },
        methods: {
            handleClose() {
                this.isOpen = false
                this.$emit('close-modal')
            },

            back() {
                this.step--;
                this.validationMessage = '';
            },

            async next() {
                this.isLoading = true;
                

                console.log('next', this.step, this.categories.length);

                if (this.step === 0) {
                    if (!this.selectedGroup) {
                        this.validationMessage = 'Please select or enter a venue group.';
                        return;
                    }

                    if (!this.isExistingGroup) {
                        if (!this.clientId) {
                            this.validationMessage = 'Please enter Client ID.';
                            return;
                        }
                        if (!this.handlVenueId) {
                            this.validationMessage = 'Please enter Handl Venue ID.';
                            return;
                        }
                        if (!this.clientSecret) {
                            this.validationMessage = 'Please enter Client Secret.';
                            return;
                        }
                    }

                    const res = await api.insertHandlGroupAndVenue({
                        venueName: this.selectedGroup,
                        handlVenueId: this.handlVenueId,
                        clientId: this.clientId,
                        clientSecret: this.clientSecret
                    })

                    if (res && res.data.code != 10000) {
                        this.step = 1;
                    } else {
                        this.validationMessage = 'Something went wrong, please try again.';
                        return;
                    }

                } else if (this.step === 1) {
                    if (this.profitType != 0 && this.profitType != 1) {
                        this.validationMessage = 'Please select profit type.';
                        return;
                    }
                    if (this.inputAmount === '') {
                        this.validationMessage = `Please input ${this.profitType === 0 ? 'Dollar' : 'Percentage'} amount.`;
                        return;
                    }
                    if (this.categories.length <= 0) {
                        this.processInitialization();
                        return;
                    } else {
                        this.step = 2;
                    }
                 
                } else if (this.step === 2 && this.categories.length > 0) {
                    if (!this.revenueGroupId) {
                        this.validationMessage = 'Please select revenue group';
                        return;
                    }
                    if (this.revenueGroupId === 'food') {
                        this.step = 3;
                    } else if (this.revenueGroupId === 'beverage') {
                        this.step = 4;
                    } else {
                        this.step = 3;
                    }
                } else if (this.step === 3 && this.categories.length > 0) {
                    if (this.checkedFood.length <= 0) {
                        this.validationMessage = 'Please select foods';
                        return;
                    }
                    if (this.revenueGroupId === 'both') {
                        this.step = 4;
                    } else {
                        this.processInitialization();
                    }
                } else if (this.step === 4 && this.categories.length > 0) {
                    if (this.checkedBeverage.length <= 0) {
                        this.validationMessage = 'Please select beverages';
                        return;
                    }
                    this.processInitialization();
                }

                this.validationMessage = '';
                this.isLoading = false;
            },

            checkedAllFood() {
                if (!this.isCheckedFood) {
                    this.checkedFood = [];
                } else {
                    this.checkedFood = this.categories;
                }
            },

            checkedAllBeverage() {
                if (!this.isCheckedBeverage) {
                    this.checkedBeverage = [];
                } else {
                    this.checkedBeverage = this.categories;
                }
            },

            handleSelectedFood(e) {
                this.checkedFood = e;
                this.isCheckedFood = false;
            },

            handleSelectedBeverage(e) {
                this.checkedBeverage = e;
                this.isCheckedBeverage = false;
            },

            querySearch(queryString, cb) {
                const results = queryString
                    ? this.groupList.filter(group => 
                        group.value.toLowerCase().includes(queryString.toLowerCase()))
                    : this.groupList;
                cb(results);
            },

            handleGroupSelect(item) {
                console.log('handleGroupSelect', item);
                if (item) {
                    this.isExistingGroup = true;
                    this.clientId = '';
                    this.clientSecret = '';
                }
            },

            handleGroupChange(value) {
                // Check if the entered value matches any group in the list
                const exists = this.groupList.some(group => 
                    group.value.toLowerCase() === value.toLowerCase()
                );
                this.isExistingGroup = exists;
                
                // Clear credentials if group doesn't exist
                if (!exists) {
                    this.clientId = '';
                    this.clientSecret = '';
                }
            },

            processInitialization() {
                this.isLoading = true;


                let data = {
                    profitTarget:  {
                        profitTargetType: this.profitType,
                        type: 0,
                        profitTarget: this.inputAmount,
                    },
                    handlVenueId: this.handlVenueId,
                    revenueGroup: this.revenueGroupId,
                    foodIds: this.revenueGroupId === 'food' || this.revenueGroupId === 'both' ? this.checkedFood.map(item => item.id) : [],
                    beverageIds: this.revenueGroupId === 'beverage' || this.revenueGroupId === 'both' ? this.checkedBeverage.map(item => item.id) : []
                }


                 api.initHandl(data).then((res) => {
                    res = res.data;
                    this.isLoading = false;
                    if (res.code != 0) {
                        utils.alert2('', `H&L initialization ${res.message}`, 'error');
                        return;
                    } 

                    this.isOpen = false;
                    utils.alert2('', 'H&L initialization success', 'success');
                    // this.$emit('handl-bind', data);
                    
                }).catch((err) => {
                    utils.alert2('', err, 'error');
                    this.isLoading = false;
                });



                // api.initHandlv2(data).then((res) => {
                //     res = res.data;
                //     this.isLoading = false;
                //     if (res.code != 0) {
                //         utils.alert2('', `H&L initialization ${res.message}`, 'error');
                //         return;
                //     } 

                //     this.isOpen = false;
                //     utils.alert2('', 'H&L initialization success', 'success');
                //     this.$emit('handl-bind', data);
                    
                // }).catch((err) => {
                //     utils.alert2('', err, 'error');
                //     this.isLoading = false;
                // });
            },

            async getHandlRevenueGroups() {
                if (env.NAME == 'lucas') {
                    return
                }
                try {
                    const res = await api.getHandlRevenueGroups();
                    if (res && res.data) {
                        this.categories = res.data.map(item => ({
                            id: item.revenueGroupId,
                            name: item.revenueGroupName
                        }));
                    } else {
                        utils.alert2('', 'Error fetching categories', 'error');
                    }
                } catch (error) {
                    utils.alert2('', 'Error fetching categories', 'error');
                }
            }
        },
    }
</script>



// WEBPACK FOOTER //
// src/components/modal/handlConfiguration.vue